import { useMemo } from "react";
import { Download, FileSpreadsheet, Loader2, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import type { ColunaExport, FormatoExport } from "@/lib/exportar-tabela";
import { ordenarTitulos, usePreferenciasDeExportacao } from "@/lib/use-preferencias-de-exportacao";
import { ListaDeColunas } from "./ListaDeColunas";

const FORMATOS: { valor: FormatoExport; rotulo: string; dica: string }[] = [
  { valor: "xlsx", rotulo: "Excel (.xlsx)", dica: "Abre direto no Excel" },
  { valor: "csv", rotulo: "CSV (.csv)", dica: "Pra outros sistemas" },
];

const linkCls = "text-xs font-semibold text-brand-700 hover:underline disabled:opacity-40 disabled:no-underline dark:text-primary";

/** Antes de baixar: formato, quais colunas e em que ordem. A escolha fica lembrada por tela. */
export function ExportarDialog<T>({
  aberto,
  onFechar,
  tela,
  titulo,
  quantidade,
  colunas,
  reimportavel,
  ocupado,
  onExportar,
}: {
  aberto: boolean;
  onFechar: () => void;
  tela: string;
  titulo: string;
  quantidade?: number;
  colunas: ColunaExport<T>[];
  reimportavel: boolean;
  ocupado: boolean;
  onExportar: (escolha: { colunas: ColunaExport<T>[]; formato: FormatoExport; incluirId: boolean }) => void;
}) {
  const { prefs, mudar, restaurar } = usePreferenciasDeExportacao(tela);
  const titulos = useMemo(() => ordenarTitulos(colunas.map((c) => c.titulo), prefs.ordem), [colunas, prefs.ordem]);
  const ocultas = prefs.ocultas.filter((t) => titulos.includes(t));
  const marcadas = titulos.length - ocultas.length;
  const porTitulo = useMemo(() => new Map(colunas.map((c) => [c.titulo, c])), [colunas]);

  const alternar = (t: string) => mudar({ ocultas: ocultas.includes(t) ? ocultas.filter((o) => o !== t) : [...ocultas, t] });
  const exportar = () =>
    onExportar({
      colunas: titulos.filter((t) => !ocultas.includes(t)).map((t) => porTitulo.get(t)!),
      formato: prefs.formato,
      incluirId: reimportavel && prefs.incluirId,
    });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && !ocupado && onFechar()}>
      <DialogContent className="flex max-h-[88dvh] max-w-lg flex-col gap-4 overflow-hidden border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle>Exportar {titulo}</DialogTitle>
          <DialogDescription>
            {quantidade != null ? `${quantidade} ${quantidade === 1 ? "linha" : "linhas"}, ` : "Sai o que está na tela, "}
            com os filtros e a ordem de agora.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2">
          {FORMATOS.map((f) => (
            <button
              key={f.valor}
              type="button"
              aria-pressed={prefs.formato === f.valor}
              onClick={() => mudar({ formato: f.valor })}
              className={cn(
                "flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors",
                prefs.formato === f.valor
                  ? "border-brand-500 bg-brand-50 dark:border-primary dark:bg-primary/10"
                  : "border-zinc-200 hover:bg-zinc-50 dark:border-border dark:hover:bg-muted",
              )}
            >
              <FileSpreadsheet className={cn("h-5 w-5 shrink-0", f.valor === "xlsx" ? "text-emerald-600" : "text-zinc-500")} />
              <span>
                <span className="block text-[13px] font-semibold text-zinc-900 dark:text-foreground">{f.rotulo}</span>
                <span className="block text-[11px] text-zinc-500 dark:text-muted-foreground">{f.dica}</span>
              </span>
            </button>
          ))}
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-semibold text-zinc-600 dark:text-muted-foreground">
              Colunas · {marcadas} de {titulos.length}
            </span>
            <span className="flex items-center gap-3">
              <button type="button" className={linkCls} onClick={() => mudar({ ocultas: marcadas === titulos.length ? titulos : [] })}>
                {marcadas === titulos.length ? "Desmarcar todas" : "Marcar todas"}
              </button>
              <button type="button" className={cn(linkCls, "inline-flex items-center gap-1")} disabled={!prefs.ordem.length && !ocultas.length} onClick={restaurar}>
                <RotateCcw className="h-3 w-3" /> Padrão
              </button>
            </span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <ListaDeColunas titulos={titulos} ocultas={ocultas} onOrdem={(ordem) => mudar({ ordem })} onAlternar={alternar} />
          </div>
        </div>

        {reimportavel && (
          <label className="flex items-start gap-2 rounded-md border border-zinc-200 p-2.5 dark:border-border">
            <input type="checkbox" checked={prefs.incluirId} onChange={(e) => mudar({ incluirId: e.target.checked })} className="mt-0.5 h-4 w-4" />
            <span className="text-[12.5px] leading-snug text-zinc-700 dark:text-foreground/80">
              Incluir ID para reimportar
              <span className="block text-[11px] text-zinc-500 dark:text-muted-foreground">
                Com o ID, a planilha editada volta pela importação atualizando os cadastros, sem duplicar.
              </span>
            </span>
          </label>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-3 dark:border-border">
          <button type="button" onClick={onFechar} disabled={ocupado} className="h-9 rounded-md px-4 text-sm text-zinc-600 hover:bg-zinc-100 disabled:opacity-50 dark:text-muted-foreground dark:hover:bg-muted">
            Cancelar
          </button>
          <button
            type="button"
            onClick={exportar}
            disabled={ocupado || marcadas === 0}
            className="inline-flex h-9 items-center gap-2 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            {ocupado ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Baixar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
